import { CommonModule } from '@angular/common'
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { MarkdownModule } from 'ngx-markdown'
import { Subscription } from 'rxjs'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import {
  AiHistoryItem,
  AiMessage,
  AiMessageAttachment,
  AiService,
  AiSession,
  AiStatus,
  AiTier,
  SendMessageAttachment,
  SendMessageResponse,
} from 'src/app/service/ai/ai.service'
import { DictionaryService } from 'src/app/service/data/dictionary.service'
import { NoteService } from 'src/app/service/data/note.service'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { UserService } from 'src/app/service/user-info/user.service'
import { ContentTypes, DaydicTypesIconNames, Media, Themes } from '../../shared/enums/enums'
import {
  DailySuggestedPrompts,
  DictionarySuggestedPrompts,
  FigureSuggestedPrompts,
  FlashCardSuggestedPrompts,
  MatchingGameSuggestedPrompts,
  NoteSuggestedPrompts,
  QuizSuggestedPrompts,
} from '../../shared/interfaces/SuggestedPrompts'

interface LingoMode {
  key: string
  label: string
  icon: string
  helpText: string
}

interface LingoModelOption {
  tier: AiTier
  label: string
  helpText: string
  // Plan required to unlock this tier (shown when locked)
  requiredPlan: string
}

interface ParsedContent {
  text: string
  dictionaries?: any[]
}

interface SelectedContent {
  type: 'dictionary' | 'note'
  id: number
  title: string
}

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  displayedContent?: string
  typing?: boolean
  mode?: string
  timestamp: Date
  feedback?: 'liked' | 'disliked' | null
  copied?: boolean
  showLibraryLink?: boolean
  parsedContent?: ParsedContent
  attachments?: AiMessageAttachment
  created_content_type?: string | null
  created_object_id?: number | null
  created_content?: string
}

@Component({
  selector: 'ddc-lingo',
  templateUrl: './ddc-lingo.component.html',
  styleUrls: ['./ddc-lingo.component.scss'],
  imports: [AngularMaterialsModules, CommonModule, FormsModule, MarkdownModule],
})
export class DdcLingoComponent implements OnInit, OnDestroy {
  readonly plus_icon = 'bolt'
  readonly pro_icon = 'diamond'
  readonly MAX_CHARS = 2000
  readonly modes: LingoMode[] = [
    {
      key: 'daily',
      label: 'Daily',
      icon: 'sunny',
      helpText: 'Get your daily vocabulary boost and practice words tailored to your level.',
    },
    {
      key: 'flashcards',
      label: 'Flashcards',
      icon: 'style',
      helpText: 'Create flashcard sets to memorize words and phrases effectively.',
    },
    {
      key: 'matching',
      label: 'Matching Game',
      icon: 'sports_esports',
      helpText: 'Generate fun matching games to test your word associations.',
    },
    {
      key: 'dictionary',
      label: 'Dictionary',
      icon: DaydicTypesIconNames.DICTIONARY,
      helpText: 'Build custom dictionaries with AI-curated words and definitions.',
    },
    {
      key: 'note',
      label: 'Note',
      icon: DaydicTypesIconNames.NOTE,
      helpText: 'Create structured study notes on any language topic.',
    },
    {
      key: 'quiz',
      label: 'Quiz',
      icon: DaydicTypesIconNames.QUIZ,
      helpText: 'Generate quizzes to test and reinforce your learning.',
    },
    {
      key: 'figure',
      label: 'Figure',
      icon: DaydicTypesIconNames.FIGURE,
      helpText: 'Create visual diagrams to map word relationships and concepts.',
    },
  ]

  readonly modelOptions: LingoModelOption[] = [
    {
      tier: 'base',
      label: 'Base',
      helpText: 'Fast, everyday model. Available on all plans.',
      requiredPlan: 'Free',
    },
    {
      tier: 'plus',
      label: 'Plus',
      helpText: 'More capable model for richer answers.',
      requiredPlan: 'Plus',
    },
    {
      tier: 'pro',
      label: 'Pro',
      helpText: 'Most advanced model for complex tasks.',
      requiredPlan: 'Pro',
    },
  ]

  getTierIcon(tier: AiTier): string {
    switch (tier) {
      case 'base':
        return 'auto_awesome'
      case 'plus':
        return 'bolt'
      case 'pro':
        return 'diamond'
      default:
        return 'auto_awesome'
    }
  }

  selectedModelTier: AiTier = 'base'

  @ViewChild('textareaRef') textareaRef!: ElementRef<HTMLTextAreaElement>
  @ViewChild('imageInput') imageInput!: ElementRef<HTMLInputElement>

  selectedMode: LingoMode
  messageText = ''
  isLoading = false
  isGenerating = false
  messages: ChatMessage[] = []
  currentSessionId?: number
  private _generatingPollTimer?: ReturnType<typeof setInterval>
  private _typewriterTimer?: ReturnType<typeof setInterval>
  _username: string = ''

  // Attachment state
  selectedImage: File | null = null
  selectedImagePreview: string | null = null
  selectedContent: SelectedContent | null = null

  // Picker dialog state
  pickerOpen = false
  pickerType: 'dictionary' | 'note' = 'dictionary'
  pickerItems: any[] = []
  pickerLoading = false
  pickerSearch = ''

  // History sidebar
  historyOpen = false
  historyItems: AiHistoryItem[] = []
  historyLoading = false
  isLoadingSession = false
  activeHistoryId?: number
  aiStatus: AiStatus | null = null

  _isLightTheme = true
  _isDarkTheme = false
  _isPaperTheme = false

  private _themeSubscription!: Subscription

  readonly CONTENT_TYPE_MAP: Record<number, { icon: string; label: string }> = {
    [ContentTypes.WORD]: { icon: 'fiber_manual_record', label: 'Word' },
    [ContentTypes.PAIR]: { icon: 'fiber_smart_record', label: 'Pair' },
    [ContentTypes.DIC]: { icon: 'auto_stories', label: 'Dictionary' },
    [ContentTypes.NOTE]: { icon: 'text_snippet', label: 'Note' },
    [ContentTypes.QUIZ]: { icon: 'question_answer', label: 'Quiz' },
    [ContentTypes.FIGURE]: { icon: 'hub', label: 'Figure' },
  }

  // readonly assetBase = Media.ASSETS

  get logoSrc(): string {
    if (this._isDarkTheme) {
      return 'assets/media/logos/lingo_logo_dark.png'
    }
    return 'assets/media/logos/lingo_logo_light.png'
  }

  get assets(): string {
    return Media.ASSETS
  }

  get charCount(): number {
    return this.messageText.length
  }

  get suggestions(): string[] {
    const map: Record<string, Record<string, string>> = {
      daily: DailySuggestedPrompts as Record<string, string>,
      flashcards: FlashCardSuggestedPrompts as Record<string, string>,
      matching: MatchingGameSuggestedPrompts as Record<string, string>,
      dictionary: DictionarySuggestedPrompts as Record<string, string>,
      note: NoteSuggestedPrompts as Record<string, string>,
      quiz: QuizSuggestedPrompts as Record<string, string>,
      figure: FigureSuggestedPrompts as Record<string, string>,
    }
    const prompts = map[this.selectedMode.key]
    return prompts ? Object.values(prompts) : []
  }

  get canSend(): boolean {
    const hasText = this.messageText.trim().length > 0
    const hasAttachment = !!this.selectedImage || !!this.selectedContent
    return (hasText || hasAttachment) && this.charCount <= this.MAX_CHARS && !this.isLoading
  }

  constructor(
    public themeService: ThemeService,
    private aiService: AiService,
    public router: Router,
    private route: ActivatedRoute,
    private user_srv: UserService,
    private dictionaryService: DictionaryService,
    private noteService: NoteService,
  ) {
    this.selectedMode = this.modes[0]

    //get if user is subscribed from this.user_info.userInfo$
    this.user_srv.userInfo$.pipe().subscribe(userInfo => {
      this._username = userInfo?.username || ''
    })
  }

  ngOnInit(): void {
    this._themeSubscription = this.themeService.theme$.subscribe(theme => {
      this._isLightTheme = theme === Themes.LIGHT
      this._isDarkTheme = theme === Themes.DARK
      this._isPaperTheme = theme === Themes.PAPER
    })
    const modeParam = this.route.snapshot.queryParamMap.get('mode')
    if (modeParam) {
      const match = this.modes.find(m => m.key === modeParam)
      if (match) this.selectedMode = match
    }
    // load user info from cache/local storage if needed here

    this.loadHistory()
    this.loadAiStatus()
  }

  ngOnDestroy(): void {
    this._themeSubscription?.unsubscribe()
    this.stopGeneratingPoll()
    this.stopTypewriter()
  }

  toggleHistory(): void {
    this.historyOpen = !this.historyOpen
    if (this.historyOpen) {
      this.loadHistory()
    }
  }

  loadHistory(): void {
    this.historyLoading = true
    this.aiService.getHistory().subscribe({
      next: items => {
        this.historyItems = items
        this.historyLoading = false
      },
      error: () => {
        this.historyLoading = false
      },
    })
  }

  loadAiStatus(): void {
    this.aiService.getAiStatus().subscribe({
      next: status => {
        this.aiStatus = status
        // If the currently selected tier is no longer allowed, reset to base.
        if (!this.isTierAllowed(this.selectedModelTier)) {
          this.selectedModelTier = 'base'
        }
      },
    })
  }

  selectSession(item: AiHistoryItem): void {
    this.activeHistoryId = item.id
    this.currentSessionId = item.id
    this.historyOpen = false
    this.aiService.getSession(item.id).subscribe({
      next: (session: AiSession | null) => {
        if (session) {
          this.messages = session.messages.map(msg => this.parseMessage(msg))
        }
      },
      error: () => {},
    })
  }

  startNewChat(): void {
    this.messages = []
    this.currentSessionId = undefined
    this.activeHistoryId = undefined
    this.historyOpen = false
  }

  selectMode(mode: LingoMode): void {
    this.selectedMode = mode
  }

  // ─── Model tier selection ────────────────────────────────

  get allowedModelTiers(): AiTier[] {
    return this.aiStatus?.allowed_model_tiers ?? ['base']
  }

  isTierAllowed(tier: AiTier): boolean {
    return this.allowedModelTiers.includes(tier)
  }

  selectModelTier(option: LingoModelOption): void {
    if (this.isTierAllowed(option.tier)) {
      this.selectedModelTier = option.tier
    } else {
      // Locked tier -> send the user to the upgrade page.
      this.router.navigate(['/upgrade'])
    }
  }

  onAttachClick(): void {
    // Context menu with disabled items for now – placeholder
  }

  // ─── Attachment methods ──────────────────────────────────

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    if (!input.files?.length) return
    const file = input.files[0]
    if (!file.type.startsWith('image/')) return
    this.selectedImage = file
    this.selectedImagePreview = URL.createObjectURL(file)
    // Reset file input so same file can be re-selected
    input.value = ''
  }

  removeSelectedImage(): void {
    if (this.selectedImagePreview) {
      URL.revokeObjectURL(this.selectedImagePreview)
    }
    this.selectedImage = null
    this.selectedImagePreview = null
  }

  removeSelectedContent(): void {
    this.selectedContent = null
  }

  triggerImagePicker(): void {
    this.imageInput?.nativeElement?.click()
  }

  openContentPicker(type: 'dictionary' | 'note'): void {
    this.pickerType = type
    this.pickerItems = []
    this.pickerSearch = ''
    this.pickerLoading = true
    this.pickerOpen = true
    this.loadPickerItems()
  }

  closeContentPicker(): void {
    this.pickerOpen = false
    this.pickerItems = []
    this.pickerSearch = ''
  }

  loadPickerItems(): void {
    this.pickerLoading = true
    if (this.pickerType === 'dictionary') {
      this.dictionaryService.get_dics(true).subscribe((res: any) => {
        this.pickerItems = Array.isArray(res) ? res : []
        this.pickerLoading = false
      })
    } else {
      this.noteService.get_notes().subscribe((res: any) => {
        this.pickerItems = Array.isArray(res) ? res : []
        this.pickerLoading = false
      })
    }
  }

  get filteredPickerItems(): any[] {
    if (!this.pickerSearch.trim()) return this.pickerItems
    const q = this.pickerSearch.toLowerCase()
    return this.pickerItems.filter((item: any) =>
      (item.title || item.label || '').toLowerCase().includes(q),
    )
  }

  selectPickerItem(item: any): void {
    this.selectedContent = {
      type: this.pickerType,
      id: item.id,
      title: item.title || item.label || 'Untitled',
    }
    this.closeContentPicker()
  }

  private getContentTypeId(type: 'dictionary' | 'note'): number {
    return type === 'dictionary' ? ContentTypes.DIC : ContentTypes.NOTE
  }

  sendMessage(): void {
    if (!this.canSend) return

    const currentImage = this.selectedImage
    const currentContent = this.selectedContent

    const userMessage: ChatMessage = {
      role: 'user',
      content: this.messageText,
      mode: this.selectedMode.key,
      timestamp: new Date(),
    }
    this.messages.push(userMessage)

    const messageContent = this.messageText
    this.messageText = ''
    this.resetTextareaHeight()
    this.isLoading = true

    // Clear attachment selections
    this.selectedContent = null
    const imagePreview = this.selectedImagePreview
    this.selectedImage = null
    this.selectedImagePreview = null

    const doSend = (uploadedImageUrl?: string): void => {
      // Build attachment object
      const attachment: SendMessageAttachment = {
        images: uploadedImageUrl ? [uploadedImageUrl] : [],
        ddc_contents: currentContent
          ? [
              {
                content_type_id: this.getContentTypeId(currentContent.type),
                object_id: currentContent.id,
              },
            ]
          : undefined,
      }

      const hasAttachment =
        (attachment.images && attachment.images.length > 0) ||
        (attachment.ddc_contents && attachment.ddc_contents.length > 0)

      this.aiService
        .sendMessage(
          messageContent,
          this.currentSessionId,
          hasAttachment ? attachment : undefined,
          {
            mode: this.selectedMode.key,
            model: this.selectedModelTier,
          },
        )
        .subscribe({
          next: (response: SendMessageResponse) => {
            // Update user message attachments if returned by the server
            if (response.attachments) {
              userMessage.attachments = response.attachments
            }

            let aiMsg: AiMessage
            if (typeof response.message === 'string') {
              aiMsg = {
                id: Date.now(),
                role: 'assistant',
                content: response.message,
                created_at: new Date().toISOString(),
              }
            } else {
              aiMsg = response.message
            }

            const parsed = this.parseMessage(aiMsg)
            const msg: ChatMessage = {
              ...parsed,
              displayedContent: '',
              typing: true,
              mode: this.selectedMode.key,
            }
            this.messages.push(msg)
            this.currentSessionId = response.session_id
            this.isLoading = false
            this.loadHistory()
            this.typewriterReveal(msg)

            const textLower = (parsed.parsedContent?.text ?? parsed.content).toLowerCase()
            // Detect "creating" pattern and start polling
            if (
              textLower.includes('i have started creating') ||
              textLower.includes("i'm now creating") ||
              textLower.includes('i am now creating')
            ) {
              this.isGenerating = true
              this.startGeneratingPoll()
            }
          },
          error: () => {
            this.messages.push({
              role: 'assistant',
              content: 'Something went wrong. Please try again.',
              displayedContent: 'Something went wrong. Please try again.',
              timestamp: new Date(),
            })
            this.isLoading = false
          },
        })
    }

    // Handle image upload first if needed
    if (currentImage) {
      const formData = new FormData()
      formData.append('file', currentImage, currentImage.name)
      this.aiService.uploadImage(formData).subscribe({
        next: (res: any) => {
          if (imagePreview) URL.revokeObjectURL(imagePreview)
          doSend(res?.url || null)
        },
        error: () => {
          if (imagePreview) URL.revokeObjectURL(imagePreview)
          doSend()
        },
      })
    } else {
      doSend()
    }
  }

  autoResize(textarea: HTMLTextAreaElement): void {
    textarea.style.height = 'auto'
    textarea.style.height = textarea.scrollHeight + 'px'
  }

  selectSuggestion(text: string): void {
    this.messageText = text
    setTimeout(() => {
      if (this.textareaRef?.nativeElement) {
        this.autoResize(this.textareaRef.nativeElement)
      }
    })
  }

  private resetTextareaHeight(): void {
    if (this.textareaRef?.nativeElement) {
      this.textareaRef.nativeElement.style.height = 'auto'
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.sendMessage()
    }
  }

  copyMessage(msg: ChatMessage): void {
    navigator.clipboard.writeText(msg.content)
    msg.copied = true
    setTimeout(() => (msg.copied = false), 2000)
  }

  likeMessage(msg: ChatMessage): void {
    msg.feedback = msg.feedback === 'liked' ? null : 'liked'
  }

  dislikeMessage(msg: ChatMessage): void {
    msg.feedback = msg.feedback === 'disliked' ? null : 'disliked'
  }

  goToLibrary(): void {
    this.router.navigate(['/library'])
  }

  parseMessage(msg: AiMessage): ChatMessage {
    let parsedContent: ParsedContent = { text: msg.content, dictionaries: [] }

    // Normalize attachments from history format
    let attachments = msg.attachments ? { ...msg.attachments } : undefined

    if (msg.attached_image) {
      if (!attachments) attachments = {}
      if (!attachments.attached_images) attachments.attached_images = []
      if (!attachments.attached_images.includes(msg.attached_image)) {
        attachments.attached_images.push(msg.attached_image)
      }
    }

    if (msg.attached_content_type && msg.attached_object_id) {
      if (!attachments) attachments = {}
      if (!attachments.attached_ddc_contents) attachments.attached_ddc_contents = []
      const exists = attachments.attached_ddc_contents.some(
        c =>
          c.content_type_id === msg.attached_content_type && c.object_id === msg.attached_object_id,
      )
      if (!exists) {
        attachments.attached_ddc_contents.push({
          content_type_id: msg.attached_content_type!,
          object_id: msg.attached_object_id!,
        })
      }
    }

    try {
      if (msg.content.trim().startsWith('{')) {
        const json = JSON.parse(msg.content)
        if (json.text || json.dictionaries || json.created_content) {
          parsedContent = {
            text: json.text || '',
            dictionaries: json.dictionaries || [],
          }
        }
      }
    } catch {
      // Not JSON, treat as plain text
    }

    return {
      role: msg.role,
      content: msg.content,
      displayedContent: parsedContent.text,
      timestamp: new Date(msg.created_at),
      parsedContent,
      attachments,
      created_content_type: msg.created_content_type,
      created_object_id: msg.created_object_id ? Number(msg.created_object_id) : null,
      created_content: msg.created_content,
    }
  }

  getContentTypeInfo(contentTypeId: number): { icon: string; label: string } {
    return this.CONTENT_TYPE_MAP[contentTypeId] ?? { icon: 'description', label: 'Content' }
  }

  getCreatedContentInfo(contentType: string | null | undefined): { icon: string; label: string } {
    if (!contentType) return { icon: 'description', label: 'Content' }
    const num = Number(contentType)
    return this.CONTENT_TYPE_MAP[num] ?? { icon: 'description', label: 'Content' }
  }

  navigateToCreatedContent(msg: ChatMessage): void {
    if (!msg.created_content_type || !msg.created_object_id) return
    const ct = Number(msg.created_content_type)
    const id = msg.created_object_id
    // TODO: add more content types and their routes as needed
    if (ct === ContentTypes.DIC) {
      this.router.navigate(['/library/dic/' + id + '/' + this._username])
    } else {
      this.router.navigate(['/library'])
    }
  }

  private typewriterReveal(msg: ChatMessage): void {
    this.stopTypewriter()
    const fullText = msg.parsedContent?.text ?? msg.content
    const charsPerTick = 3
    const intervalMs = 16
    let index = 0

    this._typewriterTimer = setInterval(() => {
      index = Math.min(index + charsPerTick, fullText.length)
      msg.displayedContent = fullText.slice(0, index)
      if (index >= fullText.length) {
        msg.typing = false
        this.stopTypewriter()
      }
    }, intervalMs)
  }

  private stopTypewriter(): void {
    if (this._typewriterTimer) {
      clearInterval(this._typewriterTimer)
      this._typewriterTimer = undefined
    }
  }

  private startGeneratingPoll(): void {
    this.stopGeneratingPoll()
    this._generatingPollTimer = setInterval(() => {
      if (!this.currentSessionId) return
      this.aiService.getSession(this.currentSessionId).subscribe({
        next: (session: AiSession | null) => {
          if (!session) return
          const lastMsg = session.messages[session.messages.length - 1]
          if (
            lastMsg &&
            lastMsg.role === 'assistant' &&
            lastMsg.content.toLowerCase().includes('i have successfully created')
          ) {
            // Update messages with all session messages
            this.messages = session.messages.map((msg, i) => {
              const parsed = this.parseMessage(msg)
              return {
                ...parsed,
                showLibraryLink: i === session!.messages.length - 1 && msg.role === 'assistant',
              }
            })
            this.isGenerating = false
            this.stopGeneratingPoll()
            this.loadHistory()
          }
        },
      })
    }, 10000)
  }

  private stopGeneratingPoll(): void {
    if (this._generatingPollTimer) {
      clearInterval(this._generatingPollTimer)
      this._generatingPollTimer = undefined
    }
  }
}
